import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  type User as FirebaseUser
} from 'firebase/auth';
import { auth } from '../../../lib/firebase';
import type { User } from '../../../shared/types';
import { ErrorService } from '../../../shared/services/error.service';
import { LoggerService } from '../../../shared/services/logger.service';

export class AuthService {
  async login(email: string, password: string): Promise<User> {
    try {
      LoggerService.info('User login attempt', 'AuthService', { email });
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      const user: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
      };
      
      LoggerService.logUserAction('login_success', 'AuthService', { email });
      return user;
    } catch (error: unknown) {
      const appError = ErrorService.handleFirebaseError(error);
      LoggerService.error('Login failed', 'AuthService', { email, error: appError });
      throw new Error(appError.message);
    }
  }

  async register(email: string, password: string, displayName?: string): Promise<User> {
    try {
      LoggerService.info('User registration attempt', 'AuthService', { email, displayName });
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Actualizar el perfil del usuario con el nombre si se proporciona
      if (displayName) {
        const { updateProfile } = await import('firebase/auth');
        await updateProfile(firebaseUser, { displayName });
      }
      
      const user: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: displayName || firebaseUser.displayName,
      };
      
      LoggerService.logUserAction('registration_success', 'AuthService', { email, displayName });
      return user;
    } catch (error: unknown) {
      const appError = ErrorService.handleFirebaseError(error);
      LoggerService.error('Registration failed', 'AuthService', { email, error: appError });
      throw appError;
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch {
      throw new Error('Error al cerrar sesión');
    }
  }

  onAuthStateChange(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        const user: User = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
        };
        callback(user);
      } else {
        callback(null);
      }
    });
  }

  private getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No existe una cuenta con este email';
      case 'auth/wrong-password':
        return 'Contraseña incorrecta';
      case 'auth/email-already-in-use':
        return 'Ya existe una cuenta con este email';
      case 'auth/weak-password':
        return 'La contraseña es muy débil';
      case 'auth/invalid-email':
        return 'Email inválido';
      case 'auth/too-many-requests':
        return 'Demasiados intentos. Intenta más tarde';
      default:
        return 'Error de autenticación';
    }
  }
}
